import { useMemo, useRef } from 'react';
import { FilterSelectedValue } from '../../../hooks/filters/interfaces/filter-selected-value.interface';
import {
  AppFilterSelectedValue,
  useAppFilterSelectedValue,
} from '../../../hooks/filters/use-app-filter-selected-value.hook';
import { deepEqual } from '../../../utils/deepEqual';

export const useAppSimpleFilterSelectedValue = (id: string) => {
  const { selectedValue } = useAppFilterSelectedValue(id);
  return selectedValue;
};
export const useAppSingleFilterSelectedLabel = (id: string) => {
  const selectedValue = useAppSimpleFilterSelectedValue(id);
  return selectedValue &&
    selectedValue.values &&
    selectedValue.values.length > 0
    ? selectedValue.values[0].label
    : '';
};

const defFilterSel: FilterSelectedValue[] = [];

export const useFullFilterSelectionMemo = (
  ...sel: AppFilterSelectedValue[]
) => {
  const prevRef = useRef<AppFilterSelectedValue[]>([]);
  const filterSetSelection = useMemo(() => {
    const ready =
      sel.find((i) => i === undefined || i.selectedValue === undefined) ===
      undefined;
    if (ready) {
      const ret: AppFilterSelectedValue[] = [];
      sel.forEach((i: any) => {
        if (i !== undefined) {
          ret.push(i);
        }
      });
      return ret;
    } else {
      return [];
    }
  }, [sel]);

  if (!deepEqual(filterSetSelection, prevRef.current)) {
    prevRef.current = filterSetSelection;
  }
  return prevRef.current;
};
/**
 * Only detect changes of filter ids spacified in includiing parameter.
 * @param including list of filter id to listen
 * @param sel
 * @returns list of selected values
 */
export const useFilterSelectionMemoFor = (
  including: string[],
  ...sel: (FilterSelectedValue | undefined)[]
) => {
  return useFilterSelectionMemo(
    ...sel.filter((i) => including.find((j) => j === i?.id)),
  );
};
export const useFilterSelectionMemo = (
  ...sel: (FilterSelectedValue | undefined)[]
) => {
  const prevRef = useRef<FilterSelectedValue[]>([]);
  const filterSelection = useMemo(() => {
    const found = sel.find((i) => i === undefined);
    const ready = found === undefined;
    if (ready) {
      const ret: FilterSelectedValue[] = [];
      sel.forEach((i: any) => {
        if (i !== undefined) {
          ret.push(i);
        }
      });
      return ret;
    } else {
      return defFilterSel;
    }
  }, [sel]);

  if (!deepEqual(filterSelection, prevRef.current)) {
    prevRef.current = filterSelection;
  }
  return prevRef.current;
};
export const useFindFilterSelection = (
  filterSelection: FilterSelectedValue[],
  findFunc: (sel: FilterSelectedValue) => boolean,
) => {
  const selectionFound = useMemo(() => {
    return filterSelection?.filter(findFunc);
  }, [filterSelection, findFunc]);

  return selectionFound;
};
export const useFindOneFilterSelection = (
  filterSelection: FilterSelectedValue[],
  findFunc: (sel: FilterSelectedValue) => boolean,
) => {
  const selectionFound = useMemo(() => {
    return filterSelection?.find(findFunc);
  }, [filterSelection, findFunc]);

  return selectionFound;
};
/**
 * This hook concatenate filter selections once those are ready.
 * @param filterSelections Arrays to concatenate
 * @returns Flat list of filter selections
 */
export const useConcatenateFilterSelection = (
  ...filterSelections: FilterSelectedValue[][]
) => {
  const prevRef = useRef<FilterSelectedValue[]>([]);
  const flatFilterSelections = useMemo(() => {
    const flatten = filterSelections.flat();
    const ready = flatten.find((i) => i === undefined) === undefined;
    if (ready) {
      return flatten;
    } else {
      return [];
    }
  }, [filterSelections]);

  if (!deepEqual(flatFilterSelections, prevRef.current)) {
    prevRef.current = flatFilterSelections;
  }
  return prevRef.current;
};

export const hasFilterSelectedValue = (
  selectedValue: FilterSelectedValue | undefined,
) => {
  if (
    selectedValue &&
    selectedValue.values &&
    selectedValue.values.length > 0
  ) {
    return selectedValue;
  } else {
    return false;
  }
};
